import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  contentChild,
  contentChildren,
  input,
  OnInit,
  signal,
  ViewEncapsulation,
  DestroyRef,
  inject
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { AppClickAreaDirective } from '../controls/input/click-area.directive';
import { AppFormFieldControl } from './form-field-control/form-field-control';

import { FormFieldLabelComponent } from './form-field-label/form-field-label.component';
import { FormFieldErrorComponent } from './form-field-error/form-field-error.component';

import { getErrorMessage } from './form-field-error/error-message';

/**
 * @deprecated Use `<im-form-field>` from `@immomio/ui/form` together with
 * the design-system input pattern instead:
 * ```html
 * <im-form-field>
 *   <label class="im:floating-label">
 *     <span floatingLabel>Label</span>
 *     <input class="im:input" type="text" formControlName="..." />
 *   </label>
 * </im-form-field>
 * ```
 * The legacy `app-form-field` will be removed once all usages have migrated.
 */
@Component({
  selector: 'app-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AppClickAreaDirective,
    NgTemplateOutlet,
    FormFieldErrorComponent,
    FormFieldLabelComponent,
    TranslatePipe
  ]
})
export class FormFieldComponent implements OnInit, AfterContentInit {
  #destroyRef = inject(DestroyRef);
  readonly label = input('');
  readonly labelBelowInput = input(false);
  readonly markCompleted = input(false);
  readonly disableErrorMessages = input(false);
  readonly disabled = input(false);
  readonly disabledClickArea = input(false);

  readonly fieldControl = contentChild(AppFormFieldControl);
  projectedLabelChild = contentChild(FormFieldLabelComponent);
  readonly projectedErrorChildren = contentChildren(FormFieldErrorComponent);
  public errors = signal<string[]>([]);
  public showRequiredMarker: boolean;
  public controlId: string;

  ngOnInit() {
    this.validateInputChild();
    this.fieldControl()
      .stateChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        if (!this.hasProjectedErrorChildren()) {
          const errorsMap = this.fieldControl().errors;
          const errors = errorsMap
            ? Object.keys(errorsMap).map(key => getErrorMessage(key))
            : [];
          this.errors.set(errors);
        }
      });
  }

  ngAfterContentInit() {
    this.showRequiredMarker =
      this.fieldControl()?.required() ||
      this.projectedLabelChild()?.showRequiredMarker();
    this.controlId = this.fieldControl()?.id();

    if (this.projectedLabelChild()) {
      this.projectedLabelChild().showRequiredMarker.set(
        this.showRequiredMarker
      );
      this.projectedLabelChild().idFor.set(this.controlId);
    }
  }

  private hasProjectedErrorChildren() {
    return this.projectedErrorChildren()?.length > 0;
  }

  private validateInputChild() {
    if (!this.fieldControl())
      throw Error('fieldControl missing in app-form-field');
  }
}
